//codex-- Mobile technician feed with separate storage, accessible actions, and the existing session/profile context.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, ChatBubbleLeftRightIcon, DocumentIcon, PaperAirplaneIcon, PaperClipIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AvatarLocation from '../components/AvatarLocation';
import { useUserData } from '../context/UserData';
import { attachmentError, createTechWallApi, TECH_WALL_ACCEPT } from './techWallApi';

//codex-- Reaction names match the PHP allowlist; the server returns the selected reaction and authoritative totals.
const REACTIONS = [
  { type: 'like', label: 'Like', emoji: '❤️' }, { type: 'thumbsUp', label: 'Thumbs Up', emoji: '👍' },
  { type: 'smile', label: 'Smile', emoji: '😊' }, { type: 'mad', label: 'Mad', emoji: '😡' },
];
const BUTTON = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50';

//codex-- Invalid dates must not crash the entire feed; valid ISO dates carry the server's timezone offset.
function PostDate({ value }) {
  const date = parseISO(value || '');
  if (!isValid(date)) return null;
  return <time dateTime={value} title={format(date, 'PPP p')} className="text-xs text-slate-500">{formatDistanceToNow(date, { addSuffix: true })}</time>;
}

//codex-- Build the mobile wall inside src/mobile, using only technician-wall API requests.
export default function TechWall() {
  const user = useUserData();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const appRoot = (process.env.REACT_APP_ROOT_URL || '').replace(/\/$/, '');
  const api = useMemo(() => createTechWallApi(apiUrl), [apiUrl]);
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyPosts, setBusyPosts] = useState({});
  const [replies, setReplies] = useState({});
  const [expanded, setExpanded] = useState({});
  const [fullText, setFullText] = useState({});
  const fileInput = useRef(null);
  const mounted = useRef(false);
  const requests = useRef(new Set());
  const pendingPosts = useRef(new Set());
  const pendingSubmit = useRef(false);
  //codex-- Keep cursor reads and mutations from racing over the same feed data.
  const feedBusy = loading || refreshing || loadingMore;
  const mutationBusy = submitting || Object.keys(busyPosts).length > 0;

  //codex-- Keep drafts on failure; session expiry follows the application's existing login route.
  const message = useCallback((failure, fallback) => {
    if (failure.response?.status === 401) { navigate('/login'); return ''; }
    return failure.response?.data?.error || failure.response?.data?.message || failure.message || fallback;
  }, [navigate]);
  const load = useCallback(async (before = null, initial = false) => {
    const controller = new AbortController();
    requests.current.add(controller);
    if (before) setLoadingMore(true);
    else if (!initial) setRefreshing(true);
    setError('');
    try {
      const data = await api.list(before, controller.signal);
      if (!mounted.current || controller.signal.aborted) return;
      setPosts((previous) => before
        ? [...previous, ...data.posts.filter((post) => !previous.some((item) => String(item.id) === String(post.id)))]
        : data.posts);
      setCursor(data.next_cursor ?? null);
    } catch (failure) {
      if (!mounted.current || controller.signal.aborted || failure.code === 'ERR_CANCELED') return;
      setError(message(failure, 'Unable to load Tech Wall. Please try again.'));
    } finally {
      requests.current.delete(controller);
      if (mounted.current && !controller.signal.aborted) { setLoading(false); setRefreshing(false); setLoadingMore(false); }
    }
  }, [api, message]);
  useEffect(() => {
    mounted.current = true;
    const activeRequests = requests.current;
    load(null, true);
    return () => { mounted.current = false; activeRequests.forEach((controller) => controller.abort()); };
  }, [load]);

  //codex-- Release image-preview URLs when replaced, removed, posted, or the page closes.
  useEffect(() => {
    if (!file || /\.pdf$/i.test(file.name)) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const chooseFile = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = '';
    if (!selected) return;
    const invalid = attachmentError(selected);
    if (invalid) { toast.error(invalid, { position: 'top-center' }); return; }
    setFile(selected);
  };

  //codex-- Prevent duplicate posting and preserve text/files until the server confirms a saved technician post.
  const submit = async (event) => {
    event.preventDefault();
    if (feedBusy || pendingSubmit.current || (!content.trim() && !file)) return;
    //codex-- Validate Unicode character limits even if a browser bypasses maxLength.
    if (Array.from(content).length > 5000) { toast.error('Posts must be 5,000 characters or fewer.', { position: 'top-center' }); return; }
    pendingSubmit.current = true;
    setSubmitting(true);
    try {
      const data = await api.add(content, file);
      if (!mounted.current) return;
      setPosts((previous) => [data.comment, ...previous]);
      setContent(''); setFile(null);
      toast.success('Posted to Tech Wall.', { position: 'top-center' });
    } catch (failure) {
      if (mounted.current) { const text = message(failure, 'Unable to post. Please try again.'); if (text) toast.error(text, { position: 'top-center' }); }
    } finally { pendingSubmit.current = false; if (mounted.current) setSubmitting(false); }
  };

  //codex-- Serialize mutations per post; a failed reply/reaction/delete never changes existing feed data.
  const actOnPost = async (postId, operation) => {
    if (feedBusy || pendingPosts.current.has(postId)) return;
    pendingPosts.current.add(postId);
    setBusyPosts((previous) => ({ ...previous, [postId]: true }));
    try { await operation(); }
    catch (failure) {
      if (mounted.current) { const text = message(failure, 'Unable to update this post.'); if (text) toast.error(text, { position: 'top-center' }); }
    } finally {
      pendingPosts.current.delete(postId);
      if (mounted.current) setBusyPosts((previous) => { const next = { ...previous }; delete next[postId]; return next; });
    }
  };
  const reply = (event, postId) => {
    event.preventDefault();
    const text = replies[postId] || '';
    if (!text.trim()) return;
    //codex-- Match the server's Unicode-aware reply limit before submitting a draft.
    if (Array.from(text).length > 2000) { toast.error('Replies must be 2,000 characters or fewer.', { position: 'top-center' }); return; }
    actOnPost(postId, async () => {
      const data = await api.reply(postId, text);
      if (!mounted.current) return;
      setPosts((previous) => previous.map((post) => post.id === postId ? { ...post, reply: [...(post.reply || []), data.reply] } : post));
      setReplies((previous) => ({ ...previous, [postId]: '' }));
      setExpanded((previous) => ({ ...previous, [postId]: true }));
    });
  };
  const react = (postId, type) => actOnPost(postId, async () => {
    const data = await api.react(postId, type);
    if (mounted.current) setPosts((previous) => previous.map((post) => post.id === postId ? { ...post, reactions: data.reactions, my_reaction: data.my_reaction } : post));
  });
  const remove = (postId) => {
    if (!window.confirm('Delete this Tech Wall post and its replies?')) return;
    actOnPost(postId, async () => {
      await api.remove(postId);
      if (mounted.current) setPosts((previous) => previous.filter((post) => post.id !== postId));
    });
  };

  //codex-- Use a single-column feed with thumb-sized controls, clear labels, and images reaching card edges.
  return (
    <main aria-label="Tech Wall" className="mx-auto min-w-0 max-w-2xl pb-8">
      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><ChatBubbleLeftRightIcon aria-hidden="true" className="h-7 w-7 text-indigo-600" />Tech Wall</h1>
          <p className="mt-1 text-sm text-slate-500">Team updates, photos, and conversations.</p>
        </div>
        <button type="button" aria-label="Refresh Tech Wall" onClick={() => load()} disabled={feedBusy || loadingMore || mutationBusy} className={`${BUTTON} shrink-0 border border-slate-200 bg-white text-slate-600`}>
          <ArrowPathIcon aria-hidden="true" className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {/* //codex-- Post text or one validated attachment; failed posts retain the entire draft. */}
      <form aria-label="New Tech Wall post" onSubmit={submit} className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-3">
          <AvatarLocation avatar={user?.avatar} className="h-10 w-10 shrink-0 rounded-full object-cover" />
          <label htmlFor="tech-wall-content" className="text-sm font-semibold text-slate-700">Share an update</label>
        </div>
        <textarea id="tech-wall-content" rows={3} maxLength={5000} value={content} onChange={(event) => setContent(event.target.value)} disabled={submitting || refreshing}
          placeholder="What's happening in the field?" className="block w-full resize-none rounded-xl border-slate-200 text-base text-slate-900 focus:border-indigo-500 focus:ring-indigo-500" />
        <input ref={fileInput} type="file" aria-label="Attach an image or PDF" accept={TECH_WALL_ACCEPT} onChange={chooseFile} disabled={submitting || refreshing} className="sr-only" tabIndex={-1} />
        {file && (
          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            {preview && <img src={preview} alt={`Preview of ${file.name}`} className="mb-2 max-h-60 w-full rounded-lg object-contain" />}
            <div className="flex items-center gap-2">
              {!preview && <DocumentIcon aria-hidden="true" className="h-6 w-6 shrink-0 text-indigo-600" />}
              <span className="min-w-0 flex-1 break-all text-sm text-slate-600">{file.name}</span>
              <button type="button" aria-label="Remove attachment" onClick={() => setFile(null)} disabled={submitting || refreshing} className={`${BUTTON} shrink-0 text-rose-600`}><XMarkIcon aria-hidden="true" className="h-5 w-5" /></button>
            </div>
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={() => fileInput.current?.click()} disabled={submitting || refreshing} className={`${BUTTON} bg-indigo-50 text-indigo-700`}><PaperClipIcon aria-hidden="true" className="h-5 w-5" />Add image or PDF</button>
          <button type="submit" disabled={feedBusy || submitting || (!content.trim() && !file)} className={`${BUTTON} bg-indigo-600 text-white hover:bg-indigo-700`}><PaperAirplaneIcon aria-hidden="true" className="h-4 w-4" />{submitting ? 'Posting...' : 'Post'}</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">JPEG, JPG, PNG, GIF, or PDF. One file, up to 10 MB.</p>
      </form>

      {/* //codex-- Provide retryable network errors and a normal empty-feed state. */}
      {error && <div role="alert" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"><p>{error}</p><button type="button" onClick={() => load()} disabled={feedBusy || mutationBusy} className={`${BUTTON} mt-2 bg-white text-rose-700`}>Try again</button></div>}
      {loading && <p role="status" className="py-8 text-center text-sm text-slate-500">Loading Tech Wall...</p>}
      {!loading && !error && posts.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center"><ChatBubbleLeftRightIcon aria-hidden="true" className="mx-auto mb-3 h-10 w-10 text-indigo-300" /><h2 className="font-semibold text-slate-800">Start the conversation</h2><p className="mt-1 text-sm text-slate-500">Share an update, photo, or document with the team.</p></div>}

      {/* //codex-- Each article uses authoritative reaction state, independently drafted replies, and ownership-aware deletion. */}
      <div className="space-y-4">
        {posts.map((post) => {
          const text = String(post.content || '');
          const isBusy = feedBusy || Boolean(busyPosts[post.id]);
          const attachment = /^tech_[a-f0-9]{32}\.(jpe?g|png|gif|pdf)$/i.test(post.file_name || '') ? `${appRoot}/tech_wall_files/${encodeURIComponent(post.file_name)}` : '';
          const thread = Array.isArray(post.reply) ? post.reply : [];
          return (
            <article key={post.id} aria-label={`Post by ${post.author}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center gap-3 p-4">
                <AvatarLocation avatar={post.avatar} className="h-10 w-10 shrink-0 rounded-full object-cover" />
                <div className="min-w-0 flex-1"><p className="break-words text-sm font-semibold text-slate-900">{post.author}</p><PostDate value={post.date} /></div>
                {String(user?.users?.id) === String(post.employee_id) && <button type="button" aria-label="Delete your post" onClick={() => remove(post.id)} disabled={isBusy} className={`${BUTTON} shrink-0 text-slate-400 hover:bg-rose-50 hover:text-rose-600`}><TrashIcon aria-hidden="true" className="h-5 w-5" /></button>}
              </div>
              {text && <div className="px-4 pb-3"><p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{fullText[post.id] || text.length <= 300 ? text : `${text.slice(0, 300)}...`}</p>{text.length > 300 && <button type="button" onClick={() => setFullText((previous) => ({ ...previous, [post.id]: !previous[post.id] }))} className={`${BUTTON} mt-1 text-indigo-600`}>{fullText[post.id] ? 'Show less' : 'Read more'}</button>}</div>}
              {attachment && (/\.pdf$/i.test(post.file_name)
                ? <a href={attachment} target="_blank" rel="noopener noreferrer" className={`${BUTTON} mx-4 mb-3 flex border border-indigo-100 bg-indigo-50 text-indigo-700`}><DocumentIcon aria-hidden="true" className="h-5 w-5" />Open attached PDF</a>
                : <a href={attachment} target="_blank" rel="noopener noreferrer" className="block"><img src={attachment} alt="Post attachment" loading="lazy" className="block h-auto w-full" /></a>)}
              <div className="border-t border-slate-100 px-3 py-2">
                <div className="flex flex-wrap gap-1" aria-label="Post reactions">
                  {REACTIONS.map((reaction) => <button key={reaction.type} type="button" aria-label={reaction.label} aria-pressed={post.my_reaction === reaction.type} disabled={isBusy} onClick={() => react(post.id, reaction.type)}
                    className={`${BUTTON} ${post.my_reaction === reaction.type ? 'border border-indigo-200 bg-indigo-50 text-indigo-700' : 'border border-transparent text-slate-500 hover:bg-slate-50'}`}><span aria-hidden="true">{reaction.emoji}</span><span>{post.reactions?.[reaction.type] || 0}</span></button>)}
                </div>
                {thread.length > 0 && <button type="button" onClick={() => setExpanded((previous) => ({ ...previous, [post.id]: !previous[post.id] }))} className={`${BUTTON} text-slate-600`}><ChatBubbleLeftRightIcon aria-hidden="true" className="h-4 w-4" />{expanded[post.id] ? 'Hide replies' : `Show replies (${thread.length})`}</button>}
              </div>
              {expanded[post.id] && thread.length > 0 && <div className="space-y-3 border-t border-slate-100 bg-slate-50 p-4">{thread.map((item) => <div key={item.id} className="flex items-start gap-2"><AvatarLocation avatar={item.avatar} className="h-7 w-7 shrink-0 rounded-full object-cover" /><div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-3"><p className="break-words text-xs font-semibold text-slate-700">{item.author}</p><p className="my-1 whitespace-pre-wrap break-words text-sm text-slate-600">{item.content}</p><PostDate value={item.date} /></div></div>)}</div>}
              <form aria-label={`Reply to ${post.author}`} onSubmit={(event) => reply(event, post.id)} className="flex items-center gap-2 border-t border-slate-100 p-3">
                <label htmlFor={`tech-wall-reply-${post.id}`} className="sr-only">Reply to {post.author}</label>
                <input id={`tech-wall-reply-${post.id}`} type="text" maxLength={2000} value={replies[post.id] || ''} onChange={(event) => setReplies((previous) => ({ ...previous, [post.id]: event.target.value }))} disabled={isBusy} placeholder="Write a reply..." className="h-11 min-w-0 flex-1 rounded-xl border-slate-200 text-base focus:border-indigo-500 focus:ring-indigo-500" />
                <button type="submit" aria-label="Send reply" disabled={isBusy || !(replies[post.id] || '').trim()} className={`${BUTTON} shrink-0 bg-indigo-50 text-indigo-700`}><PaperAirplaneIcon aria-hidden="true" className="h-5 w-5" /></button>
              </form>
            </article>
          );
        })}
      </div>
      {cursor !== null && <button type="button" onClick={() => load(cursor)} disabled={loadingMore || feedBusy || mutationBusy} className={`${BUTTON} mt-5 w-full border border-slate-200 bg-white text-slate-700`}>{loadingMore ? 'Loading...' : 'Load older posts'}</button>}
    </main>
  );
}
